import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { managerAPI } from '../lib/api';

/**
 * useRoleScope - Returns role + permission flags so shared Landlord/Manager
 * components can adaptively show/hide UI elements.
 *
 * For landlords: all permissions = true.
 * For property_managers: fetched from /api/manager/dashboard (cached).
 *
 * Also exposes `basePath` = '/landlord' or '/manager' for in-app navigation.
 */
export function useRoleScope() {
  const { user } = useAuth();
  const role = user?.role || 'guest';
  const isManager = role === 'property_manager';
  const basePath = isManager ? '/manager' : '/landlord';

  const [permissions, setPermissions] = useState(() => (isManager ? {} : ALL_TRUE));
  const [loading, setLoading] = useState(isManager);

  useEffect(() => {
    if (!isManager) {
      setPermissions(ALL_TRUE);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    managerAPI
      .getDashboard()
      .then((res) => {
        if (!alive) return;
        setPermissions(res.data?.permissions || {});
      })
      .catch(() => {
        if (!alive) return;
        setPermissions({});
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [isManager]);

  const can = (key) => Boolean(permissions[key]);

  return { role, isManager, permissions, can, basePath, loading };
}

// Landlords have everything
const ALL_TRUE = new Proxy(
  {},
  {
    get: () => true,
    has: () => true,
  }
);

export default useRoleScope;
